import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FeaturedProduct {
  id: string;
  title: string;
  description: string;
  tag: string;
  tagColor: string;
}

const featuredProducts: FeaturedProduct[] = [
  {
    id: 'coldmail-ai',
    title: 'ColdMail AI',
    description: 'Paste your background and the company you want to reach. Get a personalized cold email with a company-specific hook.',
    tag: 'Live',
    tagColor: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'
  },
  {
    id: 'linkedin-lead-scraper',
    title: 'Lead Qualifier',
    description: 'Find the right people on LinkedIn without the manual work. Extracts role, company size, and recent activity to score prospects.',
    tag: 'Live',
    tagColor: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20'
  },
  {
    id: 'contract-scanner',
    title: 'Contract Scanner',
    description: 'Upload a contract. It highlights clauses that are unusually one-sided or missing standard protections. A second pair of eyes before you sign.',
    tag: 'Beta',
    tagColor: 'bg-amber-500/15 text-amber-400 border-amber-500/20'
  },
  {
    id: 'pdf-summarizer',
    title: 'PDF Summarizer',
    description: 'Upload a 500-page PDF. Get key findings, risks, and action items in 60 seconds. No more reading 50 pages for one data point.',
    tag: 'Beta',
    tagColor: 'bg-amber-500/15 text-amber-400 border-amber-500/20'
  },
  {
    id: 'meeting-notes-ai',
    title: 'Meeting Notes AI',
    description: 'Records or uploads meeting audio. Transcribes with speaker labels, summarizes decisions, and extracts action items with owners.',
    tag: 'Beta',
    tagColor: 'bg-amber-500/15 text-amber-400 border-amber-500/20'
  },
  {
    id: 'api-tester',
    title: 'API Tester',
    description: 'Point it at your API spec. It generates test cases for happy paths, edge cases, and failure modes. Catches bugs before production.',
    tag: 'Beta',
    tagColor: 'bg-amber-500/15 text-amber-400 border-amber-500/20'
  }
];

const ProjectsSection = () => {
  return (
    <section className="relative">
      <div className="absolute inset-0 bg-gradient-to-b from-dark-500 via-dark-400 to-dark-500" />
      <div className="section-container relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Featured Products</h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            A sample of what is shipping. Browse the full catalog for all 28 tools across 7 categories.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {featuredProducts.map((product) => (
            <Link to={`/projects/${product.id}`} key={product.id} className="group block">
              <Card className="glass-card h-full border border-white/5 hover:border-tasktide-teal/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-tasktide-teal/5">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full border ${product.tagColor}`}>
                      {product.tag}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-tasktide-teal transition-colors">
                    {product.title}
                  </h3>
                  
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">
                    {product.description}
                  </p>

                  <div className="flex items-center text-tasktide-teal text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>

        <div className="text-center">
          <Button asChild variant="outline" className="border-white/10 text-white hover:bg-dark-300/50 hover:text-tasktide-teal">
            <Link to="/projects">
              View All 28 Products <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;